import { useState } from 'react'
import { RefreshCw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ConfirmDialog from '@/components/ui/confirm-dialog'

type Props = {
  count: number
  onDelete: () => Promise<void> | void
  onResetAi?: () => Promise<void> | void
  onCancel: () => void
  onClear?: () => void
}

export default function SelectionBar({ count, onDelete, onResetAi, onCancel, onClear }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const handleConfirm = async () => {
    setIsPending(true)
    await onDelete()
    setIsPending(false)
    setConfirmOpen(false)
  }

  const handleResetAi = async () => {
    if (!onResetAi) return
    setIsPending(true)
    await onResetAi()
    setIsPending(false)
  }

  return (
    <>
      {/* 固定表示のバーに一覧の最後が隠れないよう、バーの高さ（約 130px）ぶんの余白を一覧の後ろに置く */}
      <div aria-hidden className="h-36" />

      <div className="fixed bottom-20 left-0 right-0 z-40 px-4 pb-2">
        {/* max-w-md（448px）では 5 つを 1 行に並べると PC 幅でも溢れるため、常に 2 段にする */}
        <div className="max-w-md mx-auto flex flex-col gap-2 bg-card border rounded-xl shadow-lg px-4 py-3">
          {/* 1 段目：件数と、選択そのものの操作 */}
          <div className="flex items-center gap-1">
            <span className="flex-1 whitespace-nowrap text-sm font-medium">{count}件選択中</span>
            {onClear && (
              <Button variant="ghost" size="sm" className="min-h-[44px]" disabled={count === 0} onClick={onClear}>
                クリア
              </Button>
            )}
            <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={onCancel}>
              キャンセル
            </Button>
          </div>
          {/* 2 段目：選択した項目への実行操作（幅を等分） */}
          <div className="flex gap-2">
            {onResetAi && (
              <Button
                size="sm"
                disabled={count === 0 || isPending}
                onClick={handleResetAi}
                variant="outline"
                className="min-h-[44px] flex-1 gap-1.5"
              >
                <RefreshCw size={14} />
                AI再判定
              </Button>
            )}
            <Button
              size="sm"
              disabled={count === 0}
              onClick={() => setConfirmOpen(true)}
              className="min-h-[44px] flex-1 bg-destructive/10 text-destructive hover:bg-destructive/20 gap-1.5"
            >
              <Trash2 size={14} />
              削除
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`${count}件を削除しますか？`}
        description="この操作は元に戻せません。"
        confirmLabel="削除する"
        variant="destructive"
        isPending={isPending}
        onConfirm={handleConfirm}
      />
    </>
  )
}
